import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, first, Observable, of, tap } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import AppValues from 'src/app/common/app.values';
import { DefaultService as RestApiService } from 'src/app/swagger-api';

export interface CacheData<T> {
  data: T;
  userId: string;
  expiresAt: string;
}

const isCacheData = <T>(data: CacheData<T> | unknown): data is CacheData<T> => {
  if (data !== undefined && data !== null && typeof data == 'object') {
    return 'data' in data && 'userId' in data && 'expiresAt' in data;
  }

  return false;
};

@Injectable({ providedIn: 'root' })
export abstract class BaseCacheService<T> {
  protected apiService = inject(RestApiService);
  private sessionService = inject(SessionService);

  public constructor() {
    this.sessionService.sessionChanges.pipe(takeUntilDestroyed()).subscribe((session) => {
      if (!session.token) {
        localStorage.removeItem(this.getLocalStorageKey());
      }
    });
  }

  public abstract getLocalStorageKey(): string;
  protected abstract fetchFromApi(): Observable<T>;

  public getData(): Observable<T> {
    const data = this.getFromLocalStorage();

    if (data) return of(data);

    return this.fetchFromApi().pipe(
      first(),
      catchError(() => EMPTY),
      tap((response: T) => {
        this.storeInLocalStorage(response);
        return response;
      }),
    );
  }

  private getFromLocalStorage(): T | void {
    const jsonData: string | null = localStorage.getItem(this.getLocalStorageKey());

    if (!jsonData) {
      return;
    }

    const cache: CacheData<T> | unknown = JSON.parse(jsonData);

    if (!isCacheData<T>(cache)) {
      return;
    }

    if (this.isValid(cache)) {
      return cache.data;
    } else {
      localStorage.removeItem(this.getLocalStorageKey());
    }
  }

  private storeInLocalStorage(data: T): void {
    const cacheData: CacheData<T> = {
      data,
      userId: this.sessionService.userId,
      expiresAt: new Date(Date.now() + AppValues.refreshChatTokenInterval).toISOString(),
    };

    localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(cacheData));
  }

  private isValid(cacheData: CacheData<T>): boolean {
    return this.sessionService.userId === cacheData.userId && new Date(cacheData.expiresAt) > new Date();
  }
}
